import { FC, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  TextField,
  Typography,
  makeStyles,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import Alert from '@material-ui/lab/Alert';
import { useTranslation } from 'react-i18next';
import StatusBadge from './StatusBadge';
import type { RequirementThread } from './types';
import type { SplitChildInput as ApiSplitChildInput } from './api';

interface SplitPanelProps {
  /** The WIP source thread being split, with its current text. */
  source: RequirementThread;
  sourceText: string;
  /** Children currently in PENDING_SPLIT (set once a Split is proposed). */
  pendingChildren: RequirementThread[];
  /**
   * Map of childThreadId → current requirement text.
   * Populated in review re-entry so the reviewer can see each child's text
   *
   */
  childrenTexts?: Record<string, string>;
  busy: boolean;
  /**
   * Review re-entry: the panel was opened for an already-pending
   * Split (parent is PENDING_SPLIT), not a fresh proposal. The draft authoring
   * UI is skipped and only Accept/Reject is offered.
   */
  reviewMode?: boolean;
  /**
   * The role may propose, accept and reject a Split (collaborator and above).
   * Without it the review is read-only.
   */
  canWrite: boolean;
  onBack: () => void;
  onPropose: (children: ApiSplitChildInput[]) => Promise<boolean>;
  onAccept: () => Promise<boolean>;
  onReject: () => Promise<boolean>;
}

// A child row being authored before the Split is proposed.
interface ChildDraft {
  id: number;
  name: string;
  text: string;
}

const useStyles = makeStyles(theme => ({
  banner: { marginBottom: theme.spacing(2) },
  columns: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: theme.spacing(2),
  },
  sourceText: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 3,
    padding: theme.spacing(1.5),
    whiteSpace: 'pre-wrap',
    background: theme.palette.action.hover,
  },
  childRow: { marginBottom: theme.spacing(2) },
  childHead: { display: 'flex', alignItems: 'center', gap: theme.spacing(1) },
  actions: {
    display: 'flex',
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
}));

export const SplitPanel: FC<SplitPanelProps> = ({
  source,
  sourceText,
  pendingChildren,
  childrenTexts = {},
  busy,
  reviewMode = false,
  canWrite,
  onBack,
  onPropose,
  onAccept,
  onReject,
}) => {
  const classes = useStyles();
  const { t } = useTranslation('requirement-lifecycle');

  // Two empty child drafts to start (a split needs at least two).
  const nextId = useRef(0);
  const [drafts, setDrafts] = useState<ChildDraft[]>([
    { id: nextId.current++, name: '', text: '' },
    { id: nextId.current++, name: '', text: '' },
  ]);

  // In review mode the children already exist; otherwise the proposal is "live"
  // once we have pending children back from a Propose this session.
  const proposed = reviewMode || pendingChildren.length > 0;

  let bannerText: string;
  if (reviewMode) {
    bannerText = t('split.bannerReview', { count: pendingChildren.length });
  } else if (proposed) {
    bannerText = t('split.bannerPending', { count: pendingChildren.length });
  } else {
    bannerText = t('split.banner', { count: drafts.length });
  }

  const updateDraft = (i: number, patch: Partial<ChildDraft>) =>
    setDrafts(d =>
      d.map((row, idx) => (idx === i ? { ...row, ...patch } : row)),
    );
  const addDraft = () =>
    setDrafts(d => [...d, { id: nextId.current++, name: '', text: '' }]);
  const removeDraft = (i: number) =>
    setDrafts(d => (d.length > 2 ? d.filter((_, idx) => idx !== i) : d));

  const canPropose =
    drafts.length >= 2 &&
    drafts.every(d => d.name.trim() !== '' && d.text.trim() !== '');

  const handlePropose = () =>
    onPropose(drafts.map(d => ({ name: d.name.trim(), text: d.text.trim() })));

  return (
    <Box>
      <Button onClick={onBack} size="small">
        {t('common.buttons.back')}
      </Button>

      <Alert severity="info" className={classes.banner}>
        {bannerText}
      </Alert>

      <Box className={classes.columns}>
        {/* Left: read-only source */}
        <Card>
          <CardContent>
            <Typography variant="subtitle2">
              {t('split.sourceTitle')}
            </Typography>
            <Box
              display="flex"
              alignItems="center"
              style={{ gap: 8, margin: '6px 0' }}
            >
              <strong>{source.name || `#${source.threadId}`}</strong>
              <StatusBadge status={source.status} />
            </Box>
            <div className={classes.sourceText}>{sourceText || '—'}</div>
          </CardContent>
        </Card>

        {/* Right: children (editable drafts before propose; read-only after) */}
        <Card>
          <CardContent>
            <Typography variant="subtitle2">
              {t('split.childrenTitle')}
            </Typography>
            {proposed ? (
              pendingChildren.map(child => (
                <Box key={child.threadId} className={classes.childRow}>
                  <Box className={classes.childHead}>
                    <strong>{child.name || `#${child.threadId}`}</strong>
                    <StatusBadge status={child.status} />
                  </Box>
                  {childrenTexts[child.threadId] && (
                    <Typography variant="body2" color="textSecondary">
                      {childrenTexts[child.threadId]}
                    </Typography>
                  )}
                </Box>
              ))
            ) : (
              <>
                {drafts.map((d, i) => (
                  <Box key={d.id} className={classes.childRow}>
                    <Box className={classes.childHead}>
                      <TextField
                        label={t('split.childName')}
                        value={d.name}
                        onChange={e => updateDraft(i, { name: e.target.value })}
                        fullWidth
                        disabled={busy}
                        inputProps={{ 'data-testid': `split-child-name-${i}` }}
                      />
                      <IconButton
                        size="small"
                        aria-label="remove"
                        onClick={() => removeDraft(i)}
                        disabled={busy || drafts.length <= 2}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    <TextField
                      label={t('split.childText')}
                      value={d.text}
                      onChange={e => updateDraft(i, { text: e.target.value })}
                      fullWidth
                      multiline
                      minRows={2}
                      variant="outlined"
                      disabled={busy}
                      style={{ marginTop: 8 }}
                      inputProps={{ 'data-testid': `split-child-text-${i}` }}
                    />
                  </Box>
                ))}
                <Button
                  size="small"
                  startIcon={<AddIcon fontSize="small" />}
                  onClick={addDraft}
                  disabled={busy}
                >
                  {t('split.addChild')}
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </Box>

      {canWrite && (
        <Box className={classes.actions}>
          {proposed ? (
            <>
              <Button
                variant="contained"
                color="primary"
                onClick={onAccept}
                disabled={busy}
                endIcon={busy ? <CircularProgress size={18} /> : undefined}
              >
                {t('split.accept')}
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                onClick={onReject}
                disabled={busy}
              >
                {t('split.reject')}
              </Button>
            </>
          ) : (
            <Button
              variant="contained"
              color="primary"
              onClick={handlePropose}
              disabled={busy || !canPropose}
              endIcon={busy ? <CircularProgress size={18} /> : undefined}
            >
              {t('split.propose', { count: drafts.length })}
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
};

export default SplitPanel;
